import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Portal,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { toast } from 'react-toastify';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import styles from './SendPaymentMessageModal.module.scss';

const formatNumber = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildHebrewMessage = ({
  clientName,
  accountantName,
  periodLabel,
  netVat,
  incomeTaxAdvances,
  includeVat,
  includeAdvances,
}) => {
  const lines = [];
  const client = (clientName || '').trim();
  const accountant = (accountantName || '').trim();

  lines.push(`שלום רב${client ? ` ${client}` : ''},`);
  lines.push('');
  lines.push(
    `בהמשך לדיווח התקופתי, להלן פירוט סכומי התשלום המגיעים לתקופת ${periodLabel}:`
  );
  lines.push('');

  let total = 0;
  if (includeVat) {
    const vatNum = Number(netVat) || 0;
    const vatLabel = vatNum < 0 ? 'מע״מ לחזר' : 'מע״מ לתשלום';
    const vatDisplay = formatNumber(Math.abs(vatNum));
    lines.push(`${vatLabel}: ${vatDisplay} ₪`);
    total += vatNum;
  }
  if (includeAdvances) {
    lines.push(`מקדמות מס הכנסה: ${formatNumber(incomeTaxAdvances)} ₪`);
    total += Number(incomeTaxAdvances) || 0;
  }

  if (includeVat && includeAdvances) {
    lines.push('');
    lines.push(`סה״כ לתשלום: ${formatNumber(total)} ₪`);
  }

  lines.push('');
  if (includeVat && (Number(netVat) || 0) < 0) {
    lines.push('הסכום יוחזר בהתאם לנהלי הרשות.');
  } else {
    lines.push('נודה לך על ביצוע התשלום במועד.');
  }
  lines.push('לכל שאלה או הבהרה — אנו לרשותך ונשמח לסייע.');
  lines.push('');
  lines.push('בכבוד רב,');
  if (accountant) {
    lines.push(accountant);
  }
  return lines.join('\n');
};

const paymentLabelFor = (payment, amount) =>
  (Number(amount) || 0) < 0 && payment.refundLabel ? payment.refundLabel : payment.label;

/** Refunds (negative amounts) are listed but never reduce the total the client has to pay. */
const summarizePayments = (payments, amounts) => {
  const lines = payments
    .map((p) => ({ ...p, amount: Number(amounts[p.key]) || 0 }))
    .filter((p) => p.amount !== 0)
    .map((p) => ({ ...p, label: paymentLabelFor(p, p.amount) }));
  const payable = lines.filter((p) => p.amount > 0);
  return {
    lines,
    payable,
    hasRefund: lines.some((p) => p.amount < 0),
    total: payable.reduce((sum, p) => sum + p.amount, 0),
  };
};

const buildPaymentsMessage = ({ clientName, accountantName, periodLabel, payments, amounts }) => {
  const { lines: items, payable, hasRefund, total } = summarizePayments(payments, amounts);
  const lines = [];
  const client = (clientName || '').trim();
  const accountant = (accountantName || '').trim();

  lines.push(`שלום רב${client ? ` ${client}` : ''},`);
  lines.push('');
  lines.push(`בהמשך לדיווח התקופתי, להלן פירוט סכומי התשלום המגיעים לתקופת ${periodLabel}:`);
  lines.push('');
  items.forEach((p) => lines.push(`${p.label}: ${formatNumber(Math.abs(p.amount))} ₪`));
  if (payable.length > 1) {
    lines.push('');
    lines.push(`סה״כ לתשלום: ${formatNumber(total)} ₪`);
  }

  lines.push('');
  if (total > 0) lines.push('נודה לך על ביצוע התשלום במועד.');
  if (hasRefund) lines.push('סכום ההחזר יוחזר בהתאם לנהלי הרשות.');
  lines.push('לכל שאלה או הבהרה — אנו לרשותך ונשמח לסייע.');
  lines.push('');
  lines.push('בכבוד רב,');
  if (accountant) {
    lines.push(accountant);
  }
  return lines.join('\n');
};

const SendPaymentMessageModal = ({
  open,
  onClose,
  t,
  direction = 'rtl',
  clientName = '',
  accountantName = '',
  periodLabel = '',
  defaultNetVat = 0,
  defaultIncomeTaxAdvances = 0,
  includeVat = true,
  includeAdvances = true,
  caseId = '',
  onSend,
  onSendWhatsApp,
  payments = null,
}) => {
  const [netVat, setNetVat] = useState(defaultNetVat || 0);
  const [incomeTaxAdvances, setIncomeTaxAdvances] = useState(defaultIncomeTaxAdvances || 0);
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [message, setMessage] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [sending, setSending] = useState(null);
  const isPaymentsMode = Array.isArray(payments);

  const autoMessage = useMemo(
    () =>
      isPaymentsMode
        ? buildPaymentsMessage({ clientName, accountantName, periodLabel, payments, amounts: paymentAmounts })
        : buildHebrewMessage({
          clientName,
          accountantName,
          periodLabel,
          netVat,
          incomeTaxAdvances,
          includeVat,
          includeAdvances,
        }),
    [isPaymentsMode, payments, paymentAmounts, clientName, accountantName, periodLabel, netVat, incomeTaxAdvances,
      includeVat, includeAdvances]
  );

  useEffect(() => {
    if (open) {
      setNetVat(defaultNetVat || 0);
      setIncomeTaxAdvances(defaultIncomeTaxAdvances || 0);
      setIsEdited(false);
    }
  }, [open, defaultNetVat, defaultIncomeTaxAdvances]);

  // Seed once per opening; `payments` is rebuilt on every parent render.
  useEffect(() => {
    if (open && isPaymentsMode) {
      setPaymentAmounts(Object.fromEntries(payments.map((p) => [p.key, Math.round(Number(p.amount) || 0)])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPaymentsMode]);

  useEffect(() => {
    if (!isEdited) {
      setMessage(autoMessage);
    }
  }, [autoMessage, isEdited]);

  const handleNumberChange = (setter) => (e) => {
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || raw === '-') {
      setter(0);
      return;
    }
    const num = Number(raw);
    if (!isNaN(num)) {
      setter(num);
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setIsEdited(true);
  };

  const handleResetMessage = () => {
    setIsEdited(false);
    setMessage(autoMessage);
  };

  const getPaymentLabel = () => {
    if (isPaymentsMode) {
      const { lines, payable } = summarizePayments(payments, paymentAmounts);
      return (payable.length ? payable : lines).map((p) => p.label).join(', ');
    }
    if (includeVat) {
      return (Number(netVat) || 0) < 0 ? 'מעמ לחזר' : 'מעמ לתשלום';
    }
    return t('IncomeTaxAdvances') || 'מקדמות מס הכנסה';
  };

  const getAmountFormatted = () => {
    if (isPaymentsMode) {
      const { lines, payable, total } = summarizePayments(payments, paymentAmounts);
      return formatNumber(payable.length ? total : Math.abs(lines[0]?.amount || 0));
    }
    const raw = includeVat ? Math.abs(Number(netVat) || 0) : Number(incomeTaxAdvances) || 0;
    return formatNumber(raw);
  };

  const hasPaymentsToSend = !isPaymentsMode || summarizePayments(payments, paymentAmounts).lines.length > 0;
  const isRtl = direction === 'rtl';
  const textAlign = isRtl ? 'right' : 'left';
  const inputStyle = useMemo(() => ({ style: { textAlign } }), [textAlign]);
  const plainInputProps = useMemo(() => ({ disableUnderline: true }), []);

  const handleSendChat = async () => {
    if (!message || !message.trim()) {
      toast.error(t('MessageCannotBeEmpty') || 'Message cannot be empty');
      return;
    }
    if (typeof onSend !== 'function') {
      toast.error(t('ErrorSendingMessageToChat'));
      return;
    }
    try {
      setSending('chat');
      await onSend(message);
      onClose();
    } catch (err) {
      console.error('Error sending payment message to chat:', err);
      toast.error(t('ErrorSendingMessageToChat'));
    } finally {
      setSending(null);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!caseId) {
      toast.error(t('ErrorSendingWhatsApp') || 'Missing client');
      return;
    }
    if (typeof onSendWhatsApp !== 'function') {
      toast.error(t('ErrorSendingWhatsApp') || 'WhatsApp send is not available');
      return;
    }
    try {
      setSending('whatsapp');
      await onSendWhatsApp({
        caseId,
        periodLabel,
        paymentLabel: getPaymentLabel(),
        amountFormatted: getAmountFormatted(),
      });
      toast.success(t('PaymentReminderSentViaWhatsApp') || 'Message sent via WhatsApp');
      onClose();
    } catch (err) {
      console.error('Error sending payment message via WhatsApp:', err);
      toast.error(err?.message || t('ErrorSendingWhatsApp'));
    } finally {
      setSending(null);
    }
  };

  if (!open) return null;

  const renderAmountField = (key, label, value, onChange) => (
    <div key={key} className={styles.field}>
      <span className={modalStyles.fieldLabel} dir={direction}>{label}</span>
      <TextField
        value={value}
        onChange={onChange}
        fullWidth
        size="small"
        type="number"
        variant="standard"
        className={fieldStyles.styleInput}
        InputProps={{
          ...plainInputProps,
          endAdornment: <InputAdornment position="end">₪</InputAdornment>,
        }}
        inputProps={inputStyle}
      />
    </div>
  );

  return (
    <Portal>
      <div
        className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}
        onClick={sending ? undefined : onClose}
      >
        <div dir={direction} onClick={(e) => e.stopPropagation()}>
          <div className={modalStyles.modal}>
            <div className={modalStyles.body}>
              <div className={modalStyles.header}>
                <Tooltip title={t('Close') || 'Close'}>
                  <span>
                    <IconButton
                      onClick={onClose}
                      disabled={sending}
                      className={modalStyles.closeIcon}
                      aria-label={t('Close') || 'Close'}
                    >
                      <CancelIcon className={modalStyles.closeIcon} />
                    </IconButton>
                  </span>
                </Tooltip>
                <div>{t('SendPaymentMessage') || 'Send Payment Message'}</div>
                <div />
              </div>

              <div className={modalStyles.scroll}>
                <div className={styles.fields}>
                  <div className={styles.field}>
                    <span className={modalStyles.fieldLabel} dir={direction}>
                      {t('Period') || 'Period'}
                    </span>
                    <TextField
                      value={periodLabel}
                      fullWidth
                      size="small"
                      variant="standard"
                      className={fieldStyles.styleInput}
                      InputProps={{ ...plainInputProps, readOnly: true }}
                      inputProps={inputStyle}
                    />
                  </div>

                  {isPaymentsMode && payments.map((payment) =>
                    renderAmountField(
                      payment.key,
                      paymentLabelFor(payment, paymentAmounts[payment.key]),
                      paymentAmounts[payment.key] ?? 0,
                      handleNumberChange((value) =>
                        setPaymentAmounts((prev) => ({ ...prev, [payment.key]: value })))
                    )
                  )}

                  {!isPaymentsMode && includeVat && renderAmountField(
                    'vat',
                    (Number(netVat) || 0) < 0 ? 'מעמ לחזר' : 'מעמ לתשלום',
                    netVat,
                    handleNumberChange(setNetVat)
                  )}

                  {!isPaymentsMode && includeAdvances && renderAmountField(
                    'advances',
                    t('IncomeTaxAdvances'),
                    incomeTaxAdvances,
                    handleNumberChange(setIncomeTaxAdvances)
                  )}

                  <div className={styles.previewHeader}>
                    <p className={modalStyles.sectionTitle}>
                      {t('MessagePreview') || 'Message Preview'}
                    </p>
                    {isEdited && (
                      <button type="button" className={modalStyles.ghostBtn} onClick={handleResetMessage}>
                        {t('ResetToAuto') || 'Reset to auto'}
                      </button>
                    )}
                  </div>

                  <TextField
                    value={message}
                    onChange={handleMessageChange}
                    fullWidth
                    multiline
                    minRows={8}
                    maxRows={16}
                    dir="rtl"
                    variant="standard"
                    className={styles.messageInput}
                    InputProps={plainInputProps}
                  />
                </div>
              </div>

              <div className={modalStyles.footer}>
                <button
                  type="button"
                  className={modalStyles.secondaryBtn}
                  onClick={onClose}
                  disabled={sending}
                >
                  {t('Cancel') || 'Cancel'}
                </button>
                {typeof onSendWhatsApp === 'function' && (
                  <button
                    type="button"
                    className={styles.whatsappBtn}
                    onClick={handleSendWhatsApp}
                    disabled={sending || !hasPaymentsToSend}
                  >
                    {sending === 'whatsapp' ? (
                      <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      <>
                        <WhatsAppIcon className={styles.btnIcon} />
                        {t('SendViaWhatsApp')}
                      </>
                    )}
                  </button>
                )}
                {typeof onSend === 'function' && (
                  <button
                    type="button"
                    className={modalStyles.primaryBtn}
                    onClick={handleSendChat}
                    disabled={sending || !hasPaymentsToSend}
                  >
                    {sending === 'chat' ? (
                      <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      <>
                        <SendIcon className={styles.btnIcon} />
                        {t('SendToChat')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

SendPaymentMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  direction: PropTypes.string,
  clientName: PropTypes.string,
  accountantName: PropTypes.string,
  periodLabel: PropTypes.string,
  defaultNetVat: PropTypes.number,
  defaultIncomeTaxAdvances: PropTypes.number,
  includeVat: PropTypes.bool,
  includeAdvances: PropTypes.bool,
  caseId: PropTypes.string,
  onSend: PropTypes.func,
  onSendWhatsApp: PropTypes.func,
  /** When set, replaces the VAT / advances fields with one editable amount per payment. */
  payments: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    refundLabel: PropTypes.string,
    amount: PropTypes.number,
  })),
};

export default SendPaymentMessageModal;
